import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import { useAuth } from './AuthContext'
import { useSelectedOrganization } from './OrganizationContext'
import { getCurrentUserRoles } from '../services/AuthzService'
import { capabilitiesFromRoles } from '../utils/permissions'

/**
 * Resolves the caller's mia-flow roles (GET /api/bff/me/roles) and exposes UI capabilities.
 * Re-resolves whenever the selected org/tenant changes (roles are scoped by x-mia-acl-context).
 * Fail-closed: loading / error / no-roles => read-only. Mirrors ai-foundry's PermissionsContext.
 */
const NO_ROLES = new Set()

const buildValue = (roles, loading, error) => ({
  ...capabilitiesFromRoles(roles),
  error,
  loading,
})

const PermissionsContext = createContext(buildValue(NO_ROLES, true, null))

export function PermissionsProvider({ children }) {
  const { isAuthenticated } = useAuth()
  const { selectedOrganization } = useSelectedOrganization()

  const orgKey = selectedOrganization
    ? `${selectedOrganization.organization}/${selectedOrganization.tenant}`
    : null

  const [roles, setRoles] = useState(NO_ROLES)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isAuthenticated) {
      setRoles(NO_ROLES)
      setError(null)
      setLoading(false)
      return undefined
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    getCurrentUserRoles()
      .then((resolved) => {
        if (cancelled) { return }
        setRoles(new Set(resolved))
      })
      .catch((err) => {
        if (cancelled) { return }
        setRoles(NO_ROLES)
        // A 401 is session expiry, handled globally; don't flag it as a permissions error.
        if (!(err && err.status === 401)) {
          setError(err instanceof Error ? err : new Error('Failed to resolve user roles'))
        }
      })
      .finally(() => {
        if (!cancelled) { setLoading(false) }
      })

    return () => { cancelled = true }
  }, [isAuthenticated, orgKey])

  const value = useMemo(() => buildValue(roles, loading, error), [roles, loading, error])

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  )
}

PermissionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export function usePermissions() {
  return useContext(PermissionsContext)
}
